import React, { useState, useEffect, useRef } from "react";
import { IconButton } from "@mui/material";
import { Remove } from "@mui/icons-material";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import AutoComplete from "react-select";
import medicineService from "../../../Services/medicineService";
import dispenserPurchaseService from "../../../Services/dispenserPurchaseService";

const AddPurchase = () => {
  const [medicines, setMedicines] = useState([]);
  const [products, setProducts] = useState([]);
  const [medicinesOptions, setMedicinesOptions] = useState([]);

  const navigate = useNavigate();
  const medicineRef = useRef();

  const getMedicines = async () => {
    try {
      const response = await medicineService.fetchAll();
      setMedicines(response.medicines);
    } catch (error) {
      toast.error("Error fetching Medicines");
    }
  };

  useEffect(() => {
    if (medicines.length > 0) {
      const array = medicines.map((medicine) => ({
        value: medicine.id,
        label: `${medicine.medicine_name} (${medicine.price})`,
      }));
      setMedicinesOptions(array);
    }
  }, [medicines]);

  useEffect(() => {
    getMedicines();
    medicineRef.current?.focus();
  }, []);

  const handleSelectMedicine = (id) => {
    const medicine = medicines.find((m) => m.id === id);
    if (!medicine) {
      toast.error("Medicine not found!");
      return;
    }
    const newProduct = {
      medicine_id: medicine.id,
      itemName: medicine.medicine_name,
      quantity: 1,
      unit_price: 0.0,
      sale_price: 0.0,
      total_price: 0.0,
    };
    setProducts((prev) => [...prev, newProduct]);
  };

  const handleRemovePurchase = (index) => {
    setProducts(products.filter((_, i) => i !== index));
  };

  const handleQuantity = (index, name, value) => {
    if (name === "quantity" && Number(value) < 1) return;
    if ((name === "unit_price" || name === "sale_price") && Number(value) < 0) return;

    setProducts((prevProducts) =>
      prevProducts.map((product, i) => {
        if (i === index) {
          const newQuantity =
            name === "quantity" ? Number(value) : product.quantity;
          const newPrice =
            name === "unit_price" ? Number(value) : product.unit_price;
          return {
            ...product,
            [name]: value,
            total_price: newQuantity * newPrice,
          };
        }
        return product;
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (products.length === 0) {
      toast.error("At least one medicine must be added");
      return;
    }
    const submitData = {
      Stocks: products.map((p) => ({
        medicine_id: p.medicine_id,
        quantity: p.quantity,
        unit_price: p.unit_price,
        sale_price: p.sale_price,
      })),
    };
    try {
      await dispenserPurchaseService.create(submitData);
      toast.success("Purchase Created Successfully");
      navigate("/account/medicinestock");
    } catch (error) {
      console.error(error);
      toast.error("Failed to create purchase. Please try again.");
    }
  };

  const totalPurchasePrice = products.reduce(
    (sum, product) => sum + Number(product.total_price || 0),
    0
  );

  return (
    <div className="p-3 sm:p-6">

      <div className="bg-white border border-gray-200 rounded-xl px-4 sm:px-6 py-4 mb-4 sm:mb-5 shadow-sm">

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

          <div>
            <h1 className="text-[15px] sm:text-[17px] font-[500] text-gray-800">
              Add Medicines Purchase
            </h1>
            <p className="text-[11px] sm:text-[12px] text-gray-400 mt-0.5">
              {products.length} item{products.length !== 1 ? "s" : ""} added
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center w-full sm:w-auto">

            <div className="w-full sm:w-[280px]">
              <AutoComplete
                ref={medicineRef}
                options={medicinesOptions}
                onChange={(v) => handleSelectMedicine(v.value)}
                placeholder="Search & add medicine..."
                styles={{
                  control: (base) => ({
                    ...base,
                    fontSize: "13px",
                    borderColor: "#e5e7eb",
                    borderRadius: "8px",
                    backgroundColor: "#f9fafb",
                    boxShadow: "none",
                  }),
                }}
              />
            </div>

            <button
              onClick={handleSubmit}
              className="relative whitespace-nowrap bg-[#2F6DB4] text-white text-[13px] font-[500] px-4 py-2 rounded-lg shadow-md hover:shadow-gray-200 hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
            >
              Generate Invoice
            </button>

          </div>
        </div>
      </div>

      <div className="hidden sm:block bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-[#2F6DB4] text-white text-[11px] uppercase">
                <th className="py-3 px-4 text-left">Sr No.</th>
                <th className="py-3 px-4 text-left">Item Name</th>
                <th className="py-3 px-4 text-left">Units</th>
                <th className="py-3 px-4 text-left">Purchase Price</th>
                <th className="py-3 px-4 text-left">Sale Price</th>
                <th className="py-3 px-4 text-left">Total</th>
                <th className="py-3 px-4 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-gray-400 text-[13px]">
                    No purchases added yet
                  </td>
                </tr>
              ) : (
                products.map((product, index) => (
                  <tr key={index} className="border-t hover:bg-gray-50 text-[13px]">
                    <td className="py-3 px-4 text-gray-500">{index + 1}</td>
                    <td className="py-3 px-4 font-[500]">{product.itemName}</td>

                    <td className="py-3 px-4">
                      <input
                        type="number"
                        name="quantity"
                        value={product.quantity}
                        min="1"
                        onChange={(e) =>
                          handleQuantity(index, e.target.name, e.target.value)
                        }
                        className="w-20 bg-gray-50 border px-3 py-1.5 rounded-lg text-[13px]"
                      />
                    </td>

                    <td className="py-3 px-4">
                      <input
                        type="number"
                        name="unit_price"
                        value={product.unit_price}
                        min="0"
                        onChange={(e) =>
                          handleQuantity(index, e.target.name, e.target.value)
                        }
                        className="w-24 bg-gray-50 border px-3 py-1.5 rounded-lg text-[13px]"
                      />
                    </td>

                    <td className="py-3 px-4">
                      <input
                        type="number"
                        name="sale_price"
                        value={product.sale_price}
                        min="0"
                        onChange={(e) =>
                          handleQuantity(index, e.target.name, e.target.value)
                        }
                        className="w-24 bg-gray-50 border px-3 py-1.5 rounded-lg text-[13px]"
                      />
                    </td>

                    <td className="py-3 px-4 font-[600]">
                      {Number(product.total_price).toFixed(2)}
                    </td>

                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => handleRemovePurchase(index)}
                        className="bg-red-50 text-red-500 px-3 py-1 rounded-md hover:bg-red-100 text-[12px]"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}

              {products.length > 0 && (
                <tr className="bg-gray-50 border-t">
                  <td colSpan={5} className="text-right py-3 px-4 font-[500]">
                    Grand Total
                  </td>
                  <td className="py-3 px-4 font-bold">
                    {totalPurchasePrice.toFixed(2)}
                  </td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sm:hidden space-y-3">
        {products.length === 0 ? (
          <div className="bg-white border rounded-xl p-6 text-center text-gray-400">
            No purchases added yet
          </div>
        ) : (
          products.map((product, index) => (
            <div key={index} className="bg-white border rounded-xl p-4 shadow-sm">

              <div className="flex justify-between mb-3">
                <div>
                  <p className="text-[11px] text-[#2F6DB4] font-bold">#{index + 1}</p>
                  <p className="font-semibold text-gray-800">{product.itemName}</p>
                </div>

                <button
                  onClick={() => handleRemovePurchase(index)}
                  className="text-red-500 text-[12px]"
                >
                  Remove
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2 text-[12px]">
                <div>
                  <p className="text-gray-400">Qty</p>
                  <input
                    type="number"
                    name="quantity"
                    value={product.quantity}
                    onChange={(e) =>
                      handleQuantity(index, e.target.name, e.target.value)
                    }
                    className="w-full border rounded px-2 py-1"
                  />
                </div>

                <div>
                  <p className="text-gray-400">Purchase</p>
                  <input
                    type="number"
                    name="unit_price"
                    value={product.unit_price}
                    onChange={(e) =>
                      handleQuantity(index, e.target.name, e.target.value)
                    }
                    className="w-full border rounded px-2 py-1"
                  />
                </div>

                <div>
                  <p className="text-gray-400">Sale</p>
                  <input
                    type="number"
                    name="sale_price"
                    value={product.sale_price}
                    onChange={(e) =>
                      handleQuantity(index, e.target.name, e.target.value)
                    }
                    className="w-full border rounded px-2 py-1"
                  />
                </div>
              </div>

              <p className="mt-3 font-semibold text-gray-800">
                Total: {Number(product.total_price).toFixed(2)}
              </p>
            </div>
          ))
        )}
      </div>

    </div>
  );
};

export default AddPurchase;